var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, ButtonList, DataTable, DropdownButton, DropdownButtonContext, FlexBox, IconButton, Page, SearchInput, Skeleton, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';
import { queuesApi, } from 'apis/queues';
import { appActions } from 'app/app.actions';
import { analytics } from 'app/utils/segment';
import { AppLink } from 'components/appLink';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useCurrentUser } from 'utils/user/useCurrentUser';
export const QueuesTable = () => {
    var _a;
    const dispatch = useAppDispatch();
    const navigate = useAppNavigate();
    const { currentRole: { atLeastAdmin }, } = useCurrentUser();
    const { archiveQueues, teamsAssigningTeamsToQueues } = useFeatureFlag([
        'archive-queues',
        'teams-assigning-teams-to-queues',
    ]);
    const [sortStatus, setSortStatus] = useState({
        sortBy: 'name',
        direction: 'asc',
    });
    const { arg, items: queues, asyncStatus, hasNextPage, hasPreviousPage, changeArg, nextPage, previousPage, refetchItems, search, updateItem, removeItem, } = usePaginatedEndpoint({
        endpoint: queuesApi.endpoints.readQueues,
        itemsSelector: ({ data: { queues } }) => queues,
        initialArg: {
            is_archived: false,
            sort: { key: 'name', direction: 'asc' },
        },
    });
    const isSuccess = asyncStatus === 'fulfilled';
    const isError = asyncStatus === 'rejected';
    const isLoading = asyncStatus === 'pending';
    const filters = useMemo(() => [
        {
            key: 'all',
            label: 'All queues',
            isSelected: (arg === null || arg === void 0 ? void 0 : arg.is_archived) === undefined,
            onClick: () => changeArg({ is_archived: undefined }),
        },
        {
            key: 'active',
            label: 'Active queues',
            isSelected: (arg === null || arg === void 0 ? void 0 : arg.is_archived) === false,
            onClick: () => changeArg({ is_archived: false }),
        },
        {
            key: 'archived',
            label: 'Archived queues',
            isSelected: (arg === null || arg === void 0 ? void 0 : arg.is_archived) === true,
            onClick: () => changeArg({ is_archived: true }),
        },
    ], [arg === null || arg === void 0 ? void 0 : arg.is_archived, changeArg]);
    const selectedFilter = useMemo(() => filters.find(({ isSelected }) => isSelected), [filters]);
    const handleSearchToggle = useCallback((isOpen) => {
        if (!isOpen && (arg === null || arg === void 0 ? void 0 : arg.searchTerm)) {
            changeArg({
                searchTerm: '',
            });
        }
    }, [arg, changeArg]);
    const handleSearchSubmit = useCallback((value) => {
        search(value, { debounceTime: 300 });
    }, [search]);
    const handleChangeSort = useCallback((status) => {
        setSortStatus(status);
        changeArg({
            sort: status
                ? {
                    key: status.sortBy,
                    direction: status.direction,
                }
                : {
                    key: 'name',
                    direction: 'asc',
                },
        });
    }, [changeArg]);
    const handleGoToCreatePage = useCallback(() => {
        navigate({ path: '/settings/queues/create' });
        analytics.settingsQueuesAddQueueClicked();
    }, [navigate]);
    const handleGoToEditPage = useCallback((queue) => {
        navigate({
            path: '/settings/queues/:id',
            params: { path: { id: queue.id } },
        });
        analytics.settingsQueuesEditQueueClicked();
    }, [navigate]);
    const handleToggleArchive = useCallback((queue) => __awaiter(void 0, void 0, void 0, function* () {
        yield dispatch(appActions.showModal({
            modal: {
                type: 'settings:queueArchive',
                arg: {
                    queue,
                    goToArchivedQueues: () => changeArg({ is_archived: true }),
                },
            },
        }));
        if ((selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.key) === 'all') {
            updateItem(Object.assign(Object.assign({}, queue), { is_archived: !queue.is_archived }), (a, b) => a.id === b.id);
        }
        else {
            removeItem(queue, (a, b) => a.id === b.id);
        }
    }), [changeArg, dispatch, removeItem, updateItem, selectedFilter]);
    const columnStyle = {
        width: '15%',
    };
    return (_jsx(Page.Section, Object.assign({ title: "All queues", controls: _jsxs(_Fragment, { children: [_jsx(SearchInput, { onToggle: handleSearchToggle, onSubmit: handleSearchSubmit }), archiveQueues && (_jsx(DropdownButton, Object.assign({ dropdown: _jsx(DropdownButtonContext.Consumer, { children: ({ toggleDropdown }) => (_jsx(ButtonList, { children: filters.map(({ label, isSelected, onClick }) => (_jsx(ButtonList.Item, Object.assign({ isSelected: isSelected, onClick: () => {
                                    toggleDropdown();
                                    onClick();
                                } }, { children: label }), label))) })) }) }, { children: (_a = selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.label) !== null && _a !== void 0 ? _a : 'Filter' }))), atLeastAdmin && (_jsx(Button, Object.assign({ onClick: handleGoToCreatePage, id: "add-queue-btn" }, { children: "Add queue" })))] }) }, { children: _jsx(DataTable, { columns: [
                {
                    label: 'Name',
                    columnId: 'name',
                    sortable: true,
                    selector: (queue) => {
                        if (isLoading) {
                            return _jsx(Skeleton, { height: "14px", width: "40%" });
                        }
                        return (_jsxs(FlexBox, Object.assign({ gap: "250", alignItems: "center" }, { children: [_jsx(AppLink, Object.assign({ path: "/settings/queues/:id", params: {
                                        path: {
                                            id: queue.id,
                                        },
                                    } }, { children: _jsx(Text.Paragraph, Object.assign({ as: "span" }, { children: queue.name })) })), queue.is_default && (_jsx(Badge, Object.assign({ variant: "primary", size: "small" }, { children: "Default" }))), queue.is_archived && (_jsx(Badge, Object.assign({ variant: "error", size: "small" }, { children: "Archived" })))] })));
                    },
                },
                {
                    label: 'Priority',
                    columnId: 'priority',
                    sortable: true,
                    selector: (queue) => {
                        if (isLoading) {
                            return _jsx(Skeleton, { height: "14px", width: "20px" });
                        }
                        return queue.priority;
                    },
                    style: columnStyle,
                },
                ...(teamsAssigningTeamsToQueues
                    ? [
                        {
                            label: 'Teams',
                            selector: (queue) => {
                                if (isLoading) {
                                    return _jsx(Skeleton, { height: "14px", width: "20px" });
                                }
                                return queue.number_of_teams;
                            },
                            style: columnStyle,
                        },
                    ]
                    : []),
                {
                    label: 'Agents',
                    selector: (queue) => {
                        if (isLoading) {
                            return _jsx(Skeleton, { height: "14px", width: "20px" });
                        }
                        return queue.number_of_agents;
                    },
                    style: columnStyle,
                },
                {
                    label: '',
                    selector: (queue) => {
                        if (isLoading) {
                            return (_jsx(FlexBox, Object.assign({ justifyContent: "center", alignItems: "center", width: "32px", height: "32px" }, { children: _jsx(Skeleton, { height: "18px", width: "18px" }) })));
                        }
                        if (!archiveQueues) {
                            return (_jsx(IconButton, { "aria-label": "Edit queue", variant: "tertiary", icon: "pen", onClick: () => handleGoToEditPage(queue) }));
                        }
                        return (_jsx(DropdownButton, { icon: "dots-h", buttonProps: { variant: 'tertiary' }, noArrow: true, fixed: true, dropdown: _jsx(DropdownButtonContext.Consumer, { children: ({ toggleDropdown }) => (_jsxs(ButtonList, { children: [_jsx(ButtonList.Item, Object.assign({ onClick: () => handleGoToEditPage(queue) }, { children: "Edit" })), _jsx(Tooltip, Object.assign({ content: "The default queue cannot be archived.", disabled: !queue.is_default, dark: true }, { children: _jsx(ButtonList.Item, Object.assign({ isDisabled: queue.is_default, onClick: () => {
                                                    toggleDropdown();
                                                    handleToggleArchive(queue);
                                                } }, { children: queue.is_archived ? 'Unarchive' : 'Archive' })) }))] })) }) }));
                    },
                    style: {
                        width: 0,
                    },
                },
            ], data: asyncStatus === 'pending'
                ? Array.from({ length: 25 })
                : queues, paginationProps: {
                previousButtonDisabled: !hasPreviousPage || !isSuccess,
                previousButtonClick: previousPage,
                nextButtonDisabled: !hasNextPage || !isSuccess,
                nextButtonClick: nextPage,
            }, sortingProps: {
                sortStatus,
                onSortChange: handleChangeSort,
            }, tableStatusProps: {
                loadingFailed: isError,
                onLoadingRetry: refetchItems,
                emptyState: !queues.length && isSuccess
                    ? {
                        title: 'No queues found',
                        description: 'Try changing your filters or search criteria.',
                    }
                    : undefined,
            }, tableStyleProps: {
                stickyHeader: true,
                stickyFooter: true,
            } }) })));
};
